import { useAccountVariables } from '@/hooks/query/account-variables.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import {
  RichTextActiveFormats,
  RichTextFormat,
  RichTextToolbar,
  RichTextToolbarButton,
  RichTextVendorEditor,
  setupColorPicker,
  setupPointOfContactButton,
  setupTooltipButton,
} from '@/ui/rich-text';
import { ColorPickerIcon, TooltipIcon } from '@/utils/helpers/icons.helper';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SLIDE_FORMAT_GROUPS: RichTextFormat[][] = [
  ['bold', 'italic', 'underline', 'bullet', 'ordered', 'check', 'link'],
];

const StyledToolbar = styled(RichTextToolbar)`
  // The slide form gives the toolbar a row of its own
  width: 100%;
  margin-top: var(--xsmall-padding);
  margin-bottom: 36px !important;
  position: sticky;
  top: 0;
  z-index: 99999;
`;

// Widens on hover to reveal its label, so only the width departs from the shared metrics
const ExpandingButton = styled(RichTextToolbarButton)`
  position: relative;
  width: auto;
  min-width: 28px;
  overflow: hidden;
  white-space: nowrap;
  transition: width 0.3s ease, padding 0.3s ease;

  svg {
    flex-shrink: 0;
    display: inline-block;
  }

  .tooltip-text {
    display: inline-block;
    opacity: 0;
    max-width: 0;
    margin-left: 0;
    overflow: hidden;
    transition: opacity 0.3s ease 0.1s, max-width 0.5s ease,
      margin-left 0.3s ease;
    white-space: nowrap;
    pointer-events: none;
    color: var(--black-main);
    font-size: var(--xsmall-font-size);
    font-weight: 500;
  }

  .pi {
    flex-shrink: 0;
    font-size: var(--medium-font-size);
    color: var(--black-main);
  }

  &:hover,
  &.point-of-contact-btn--open {
    padding-left: var(--xxsmall-padding);
    padding-right: var(--xxsmall-padding);

    .tooltip-text {
      opacity: 1;
      max-width: 200px;
      margin-left: var(--xxsmall-padding);
    }
  }
`;

// The dropdown the point-of-contact button opens is positioned against this
const TrailingControls = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

// The swatch is a hidden native input the button opens
const ColorInput = styled.input`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

type SlideFormattingToolbarProps = {
  className?: string;
  activeFormats: RichTextActiveFormats;
  applyFormat: (format: string, value?: unknown) => void;
  isActive: boolean;
  vendorEditorRef: React.RefObject<RichTextVendorEditor>;
};

export const SlideFormattingToolbar: React.FC<SlideFormattingToolbarProps> = ({
  className,
  activeFormats,
  applyFormat,
  isActive,
  vendorEditorRef,
}) => {
  const { t } = useTranslation();

  const currentAccount = useAppSelector(selectCurrentAccount);
  const { canUseFeature } = useFeatureFlag();
  const showPointsOfContact = canUseFeature(
    PostHogFeatureFlags.TRAINING_VARIABLES,
  );
  const { itContactConfigured } = useAccountVariables(currentAccount?.id, {
    enabled: showPointsOfContact,
  });

  const colorButtonRef = useRef<HTMLButtonElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const tooltipButtonRef = useRef<HTMLButtonElement>(null);
  const pointOfContactButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setupColorPicker(
      vendorEditorRef,
      colorButtonRef.current,
      colorInputRef.current,
    );
    setupTooltipButton(vendorEditorRef, tooltipButtonRef.current);
    setupPointOfContactButton(
      vendorEditorRef,
      pointOfContactButtonRef.current,
      {
        isConfigured: itContactConfigured,
        canConfigure: !currentAccount?.isSystem,
      },
    );
  }, [
    vendorEditorRef,
    isActive,
    itContactConfigured,
    currentAccount?.isSystem,
  ]);

  return (
    <StyledToolbar
      className={className}
      activeFormats={activeFormats}
      onFormat={applyFormat}
      formatGroups={SLIDE_FORMAT_GROUPS}
      linkPromptLabel={t('content.richText.linkPrompt')}
      trailingControls={
        <TrailingControls>
          <ExpandingButton
            type="button"
            ref={tooltipButtonRef}
            title={t('content.addTooltip')}
          >
            <TooltipIcon />
            <span className="tooltip-text">{t('content.addTooltip')}</span>
          </ExpandingButton>
          <RichTextToolbarButton type="button" ref={colorButtonRef}>
            <ColorPickerIcon />
          </RichTextToolbarButton>
          <ColorInput type="color" ref={colorInputRef} />
          {showPointsOfContact && (
            <ExpandingButton
              type="button"
              ref={pointOfContactButtonRef}
              title={t('content.addPointOfContact')}
            >
              <i className="pi pi-user" />
              <span className="tooltip-text">
                {t('content.addPointOfContact')}
              </span>
            </ExpandingButton>
          )}
        </TrailingControls>
      }
    />
  );
};
